import { useStore } from "@nanostores/react"
import { createWebPersistentAtom } from "./persistent"
import {
  type CombinedLoanAndProductsQuery,
  type LoanApplicationInput,
  type LoanProfileType,
} from "@/generated/graphql"
import { atom } from "nanostores"

export const PROPERTIES_FLIPPED_LABELS = ["0", "1 - 2", "3 - 9", "10+"] as const
export type PropertiesFlipped = (typeof PROPERTIES_FLIPPED_LABELS)[number]

export const RENTALS_OWNED_LABELS = ["0", "1 - 2", "3 - 9", "10+"] as const
export type RentalsOwned = (typeof RENTALS_OWNED_LABELS)[number]

export const CASH_AVAILABLE_LABELS = [
  "0 - $25k",
  "$25k - $50k",
  "$50k - $100k",
  "$100k+",
] as const
export type CashAvailable = (typeof CASH_AVAILABLE_LABELS)[number]

export const CREDIT_SCORE_LABELS = [
  "Below 640",
  "640-669",
  "670-689",
  "690-749",
  "750+",
] as const
export type CreditScore = (typeof CREDIT_SCORE_LABELS)[number]

// Loan Conversion Values
export const PROPERTIES_FLIPPED_LOAN_VALUES = [0, 1, 3, 10] as const
export const RENTALS_OWNED_LOAN_VALUES = [0, 1, 3, 10] as const
export const CASH_AVAILABLE_LOAN_VALUES = [
  "0-25",
  "25-50",
  "50-100",
  "100+",
] as const
export const CREDIT_SCORE_LOAN_VALUES = [
  "Below 640",
  "640-669",
  "670-689",
  "690-749",
  "Above 750",
] as const

// LoanProfile Conversion Values
export const PROPERTIES_FLIPPED_PROFILE_VALUES = [
  "0",
  "1-2",
  "3-9",
  "10+",
] as const
export const RENTALS_OWNED_PROFILE_VALUES = ["0", "1-2", "3-9", "10+"] as const
export const CASH_AVAILABLE_PROFILE_VALUES = [
  "0-25",
  "25-50",
  "50-100",
  "100+",
] as const
export const CREDIT_SCORE_PROFILE_VALUES = [
  "Below 640",
  "640-669",
  "670-689",
  "690-749",
  "750+",
] as const

export interface PreQualSharedState {
  propertiesFlipped?: PropertiesFlipped | null
  rentalsOwned?: RentalsOwned | null
  cashAvailable?: CashAvailable | null
  creditScore?: CreditScore | null
  borrower?: string | null
  borrowerNames?: string[] | null
}

export interface ProfilePreQualState extends PreQualSharedState {
  intentStatus?: string | null
}

export interface LoanAppPreQualState extends PreQualSharedState {
  contractStatus?: string | null
  targetCloseDate?: string | null
  originalPurchaseDate?: string | null
}

export type PreQualState = ProfilePreQualState & LoanAppPreQualState

const getInitialPreQualState = (): PreQualState => {
  return {} as PreQualState
}

export const $preQual = createWebPersistentAtom<PreQualState>(
  "preQual:",
  getInitialPreQualState()
)

export const $activePreQual = atom<Array<keyof PreQualState>>([])

export const setPreQualState = (state: PreQualState) => {
  console.trace("preQual state set", state)
  $preQual.set({
    ...$preQual.get(),
    ...state,
  })
}

export const updatePreQual = <K extends keyof PreQualState>(
  key: K,
  value: PreQualState[K]
) => {
  if (key === "borrower" && Array.isArray(value)) {
    $preQual.set({
      ...$preQual.get(),
      borrower: value[0],
      borrowerNames:
        typeof value[1] === "string" ? [value[1]] : (value[1] as string[]),
    })
  } else {
    $preQual.set({
      ...$preQual.get(),
      [key]: value,
    })
  }
}

export const clearPreQualState = () => {
  $preQual.set(getInitialPreQualState())
}

export const initPreQualFromLoanApplication = (
  data: NonNullable<CombinedLoanAndProductsQuery["loanApplication"]>
) => {
  clearPreQualState()

  if (!data) return

  const state: LoanAppPreQualState = {
    propertiesFlipped:
      typeof data.flipsCompleted === "number"
        ? PROPERTIES_FLIPPED_LABELS[
            PROPERTIES_FLIPPED_LOAN_VALUES.indexOf(
              data.flipsCompleted as (typeof PROPERTIES_FLIPPED_LOAN_VALUES)[number]
            )
          ]
        : null,
    rentalsOwned: data.rentalProperties
      ? RENTALS_OWNED_LABELS[
          RENTALS_OWNED_LOAN_VALUES.indexOf(
            data.rentalProperties as (typeof RENTALS_OWNED_LOAN_VALUES)[number]
          )
        ]
      : null,
    cashAvailable: data.cashAvailable
      ? CASH_AVAILABLE_LABELS[
          CASH_AVAILABLE_PROFILE_VALUES.indexOf(
            data.cashAvailable as (typeof CASH_AVAILABLE_PROFILE_VALUES)[number]
          )
        ]
      : null,
    creditScore: data.creditScore
      ? CREDIT_SCORE_LABELS[
          CREDIT_SCORE_PROFILE_VALUES.indexOf(
            data.creditScore as (typeof CREDIT_SCORE_PROFILE_VALUES)[number]
          )
        ]
      : null,
    borrower: data.borrower || null,
    borrowerNames: data.borrowerNames || null,

    contractStatus: data.contractStatus || null,
    originalPurchaseDate: data.originalPurchaseDate || null,
    targetCloseDate: data.targetCloseDate || null,
  }

  setPreQualState(state)
}

export const initPreQualFromProfile = (
  data?: LoanProfileType,
  clear = true
) => {
  if (clear) {
    clearPreQualState()
  }

  if (!data) return

  const state: ProfilePreQualState = {
    propertiesFlipped: data.propertiesFlipped
      ? PROPERTIES_FLIPPED_LABELS[
          PROPERTIES_FLIPPED_PROFILE_VALUES.indexOf(
            data.propertiesFlipped as (typeof PROPERTIES_FLIPPED_PROFILE_VALUES)[number]
          )
        ]
      : null,
    rentalsOwned: data.rentalsOwned
      ? RENTALS_OWNED_LABELS[
          RENTALS_OWNED_PROFILE_VALUES.indexOf(
            data.rentalsOwned as (typeof RENTALS_OWNED_PROFILE_VALUES)[number]
          )
        ]
      : null,
    cashAvailable: data.cashAvailable
      ? CASH_AVAILABLE_LABELS[
          CASH_AVAILABLE_LOAN_VALUES.indexOf(
            data.cashAvailable as (typeof CASH_AVAILABLE_LOAN_VALUES)[number]
          )
        ]
      : null,
    creditScore:
      data?.creditScore === "below-640"
        ? CREDIT_SCORE_LOAN_VALUES[0]
        : data.creditScore
          ? CREDIT_SCORE_LABELS[
              CREDIT_SCORE_LOAN_VALUES.indexOf(
                data.creditScore as (typeof CREDIT_SCORE_LOAN_VALUES)[number]
              )
            ]
          : null,
    borrower: data.borrower || null,
    borrowerNames: data.borrowerNames || null,
  }

  setPreQualState(state)
}

export const convertPreQualToLoanProfile = (
  profileState?: ProfilePreQualState
): Partial<LoanProfileType> => {
  const state = profileState || $preQual.get()
  return {
    ...state,
    propertiesFlipped: state.propertiesFlipped
      ? PROPERTIES_FLIPPED_PROFILE_VALUES[
          PROPERTIES_FLIPPED_LABELS.indexOf(state.propertiesFlipped)
        ]
      : null,
    rentalsOwned: state.rentalsOwned
      ? RENTALS_OWNED_PROFILE_VALUES[
          RENTALS_OWNED_LABELS.indexOf(state.rentalsOwned)
        ]
      : null,
    cashAvailable: state.cashAvailable
      ? CASH_AVAILABLE_PROFILE_VALUES[
          CASH_AVAILABLE_LABELS.indexOf(state.cashAvailable)
        ]
      : null,
    creditScore: state.creditScore
      ? CREDIT_SCORE_PROFILE_VALUES[
          CREDIT_SCORE_LABELS.indexOf(state.creditScore)
        ]
      : null,
    borrower: state.borrower || null,
    borrowerNames: state.borrowerNames || null,
  }
}

const dateConvertForServer = (date?: string | null | Date) => {
  if (!date) return null

  return new Date(date).toISOString()
}

export const convertPreQualToLoanApp = (
  loanAppState?: LoanAppPreQualState
): Partial<LoanApplicationInput> => {
  const state = loanAppState || $preQual.get()
  return {
    flipsCompleted: state.propertiesFlipped
      ? PROPERTIES_FLIPPED_LOAN_VALUES[
          PROPERTIES_FLIPPED_LABELS.indexOf(state.propertiesFlipped)
        ]
      : null,
    rentalProperties: state.rentalsOwned
      ? RENTALS_OWNED_LOAN_VALUES[
          RENTALS_OWNED_LABELS.indexOf(state.rentalsOwned)
        ]
      : null,
    cashAvailable: state.cashAvailable
      ? CASH_AVAILABLE_LOAN_VALUES[
          CASH_AVAILABLE_LABELS.indexOf(state.cashAvailable)
        ]
      : null,
    creditScore: state.creditScore
      ? CREDIT_SCORE_LOAN_VALUES[CREDIT_SCORE_LABELS.indexOf(state.creditScore)]
      : null,
    borrower: state.borrower || null,
    borrowerNames: state.borrowerNames || null,
    originalPurchaseDate: dateConvertForServer(state.originalPurchaseDate),
    targetCloseDate: dateConvertForServer(state.targetCloseDate),
    contractStatus: state.contractStatus || null,
  }
}

export const usePreQualState = () => {
  return useStore($preQual)
}

export const usePreQualStateAsLoanProfile = () => {
  const preQualState = usePreQualState()

  return convertPreQualToLoanProfile(preQualState)
}

export const usePreQualStateAsLoanApp = () => {
  const preQualState = usePreQualState()

  return convertPreQualToLoanApp(preQualState)
}

export const useDidPreQual = () => {
  const preQualState = usePreQualState()

  return !!preQualState?.propertiesFlipped
}

export const useFinishedPreQual = (questions?: Array<keyof PreQualState>) => {
  const preQualState = usePreQualState()
  const check = questions || [
    "propertiesFlipped",
    "rentalsOwned",
    "cashAvailable",
    "creditScore",
  ]

  return check.every((key) => !!preQualState[key])
}
